import React from 'react';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import DescriptionIcon from '@mui/icons-material/Description';
import { TIPO_SECAO_PAGINA_LABEL } from '../../../../model/apm/apm-enums';
import { ICustomPageSectionResponse } from '../../../../model/apm/custom-page/response/custom-page-section-response';
import { ICustomPageSectionItemResponse } from '../../../../model/apm/custom-page/response/custom-page-section-item-response';
import { SortableList } from '../../components/sortable-list';
import { SectionCard } from '../../components/section-card';
import { ItemRow } from '../../components/item-row';
import { StatusChips } from '../../components/status-chips';
import { EmptyState } from '../../../../components/empty-state';
import { ImageThumbnail } from '../../components/image-thumbnail';
import { AcessoRapidoIcon } from '../../components/apm-icons';
import { formatFileSize } from '../../components/document-utils';
import { htmlToPlainText } from '../../components/html-utils';
import { CUSTOM_PAGE_SECTION_CONFIG } from './custom-page-section-config';
import { CUSTOM_PAGE_SECTION_ICONS } from './custom-page-section-icons';

interface CustomPageSectionListProps {
    sections: ICustomPageSectionResponse[];
    onAddSection: () => void;
    onEditSection: (section: ICustomPageSectionResponse) => void;
    onDeleteSection: (section: ICustomPageSectionResponse) => void;
    onToggleSectionVisibility: (section: ICustomPageSectionResponse) => void;
    onReorderSections: (next: ICustomPageSectionResponse[]) => void;
    onAddItem: (section: ICustomPageSectionResponse) => void;
    onEditItem: (section: ICustomPageSectionResponse, item: ICustomPageSectionItemResponse) => void;
    onDeleteItem: (section: ICustomPageSectionResponse, item: ICustomPageSectionItemResponse) => void;
    onReorderItems: (section: ICustomPageSectionResponse, next: ICustomPageSectionItemResponse[]) => void;
}

function itemLeading(item: ICustomPageSectionItemResponse): React.ReactNode {
    if (item.icon) return <AcessoRapidoIcon name={item.icon} />;
    if (item.file) return <DescriptionIcon color="primary" fontSize="small" />;
    if (item.image) return <ImageThumbnail path={item.image} alt={item.title} />;
    return undefined;
}

function itemSubtitle(item: ICustomPageSectionItemResponse): string | undefined {
    if (item.file) return [item.fileOriginalName, formatFileSize(item.fileSize)].filter(Boolean).join(' · ') || undefined;
    return item.subtitle || htmlToPlainText(item.description) || item.url;
}

export function CustomPageSectionList(props: CustomPageSectionListProps) {
    const { sections } = props;

    if (sections.length === 0) {
        return <EmptyState icon={<ViewAgendaIcon />} message="Esta página ainda não possui seções" actionLabel="Adicionar a primeira seção" onAction={props.onAddSection} />;
    }

    return (
        <SortableList
            items={sections}
            getId={(s) => s.id}
            onReorder={props.onReorderSections}
            renderItem={(section, dragHandle) => {
                const config = CUSTOM_PAGE_SECTION_CONFIG[section.type];
                const items = section.items || [];
                return (
                    <SectionCard
                        dragHandle={dragHandle}
                        icon={CUSTOM_PAGE_SECTION_ICONS[section.type]}
                        title={section.title || TIPO_SECAO_PAGINA_LABEL[section.type]}
                        subtitle={section.subtitle || htmlToPlainText(section.content) || section.url}
                        chips={<StatusChips typeLabel={TIPO_SECAO_PAGINA_LABEL[section.type]} isVisible={section.isVisible} />}
                        isVisible={section.isVisible}
                        canDelete
                        itemCount={items.length}
                        itemNoun={config?.itemNoun}
                        onToggleVisibility={() => props.onToggleSectionVisibility(section)}
                        onEdit={() => props.onEditSection(section)}
                        onDelete={() => props.onDeleteSection(section)}
                        onAddItem={config?.itemFields ? () => props.onAddItem(section) : undefined}
                    >
                        {items.length === 0 ? (
                            <EmptyState
                                icon={CUSTOM_PAGE_SECTION_ICONS[section.type]}
                                message={`Nenhum(a) ${(config?.itemNoun || 'item').toLowerCase()} cadastrado(a) nesta seção`}
                            />
                        ) : (
                            <SortableList
                                items={items}
                                getId={(i) => i.id}
                                onReorder={(next) => props.onReorderItems(section, next)}
                                renderItem={(item, itemHandle) => (
                                    <ItemRow
                                        dragHandle={itemHandle}
                                        leading={itemLeading(item)}
                                        title={item.title || '(sem título)'}
                                        subtitle={itemSubtitle(item)}
                                        onEdit={() => props.onEditItem(section, item)}
                                        onDelete={() => props.onDeleteItem(section, item)}
                                    />
                                )}
                            />
                        )}
                    </SectionCard>
                );
            }}
        />
    );
}
